import { createReducer } from 'typesafe-actions'
import { CurrenciesCrypto, RequestSettlementResponseDto } from '@sparling/financial-common'

import { ActionTypes } from '../types'
import { LoadingState } from '../../types'
import {
  confirmReceivedAmountSuccess,
  confirmReceivedAmountFailure,
  confirmReceivedAmountRequest,
  openAddNewWalletPopup,
  openConfirmReceivedAmountPopup,
  createUserWalletSuccess,
  createUserWalletFailure,
  openAwaitingDepositPopup,
  resetAddNewWalletPopup,
  createSettlementRequest,
  createSettlementSuccess,
  createSettlementFailure,
  openRequestSettlementPopup,
} from '../actions'

export type UserWalletsState = {
  addNewWalletPopup: {
    currency: CurrenciesCrypto | null
    open: boolean
    clientAddress: string
  }
  checkClientAddressAndGenerateNewAddress: {
    address: string | null
    created: Date | null
    status: LoadingState
    error: Error | null
  }
  confirmReceivedAmount: {
    walletId: string | null
    open: boolean
    data: {
      amount: string
      walletId: string
    }
    status: LoadingState
    error: Error | null
  }
  requestSettlement: {
    open: boolean
    status: LoadingState
    data: RequestSettlementResponseDto
    error: Error | null
  }
}

export const initialState: UserWalletsState = {
  addNewWalletPopup: {
    currency: null,
    open: false,
    clientAddress: '',
  },
  checkClientAddressAndGenerateNewAddress: {
    address: null,
    created: null,
    status: 'IDLE',
    error: null,
  },
  confirmReceivedAmount: {
    walletId: null,
    open: false,
    data: {
      amount: '',
      walletId: '',
    },
    status: 'IDLE',
    error: null,
  },
  requestSettlement: {
    open: false,
    status: 'IDLE',
    data: {} as RequestSettlementResponseDto,
    error: null,
  },
}

export const userWallets = createReducer<UserWalletsState, ActionTypes>(initialState)
  .handleAction(openAddNewWalletPopup, (state, { payload: { open, currency } }) => ({
    ...state,
    addNewWalletPopup: {
      ...state.addNewWalletPopup,
      currency: currency || null,
      open,
      ...(!open && { clientAddress: '' }),
    },
  }))
  .handleAction([resetAddNewWalletPopup, createUserWalletFailure], (state) => ({
    ...state,
    addNewWalletPopup: {
      ...initialState.addNewWalletPopup,
      open: false,
    },
    checkClientAddressAndGenerateNewAddress: initialState.checkClientAddressAndGenerateNewAddress,
  }))
  .handleAction(createUserWalletSuccess, (state, { payload }) => ({
    ...state,
    checkClientAddressAndGenerateNewAddress: {
      ...state.checkClientAddressAndGenerateNewAddress,
      status: 'SUCCESS',
      address: payload?.data?.metadata?.generated?.address || null,
      created: payload?.data?.metadata?.whitelisting?.createdAt || null,
      // payload?.data?.metadata?.whitelisting?.createdAt as string
    },
  }))
  .handleAction(openAwaitingDepositPopup, (state, { payload }) => ({
    ...state,
    checkClientAddressAndGenerateNewAddress: {
      ...state.checkClientAddressAndGenerateNewAddress,
      status: 'SUCCESS',
      address: payload?.address || null,
      created: payload?.created ? new Date(payload?.created) : null,
    },
    addNewWalletPopup: {
      ...state.addNewWalletPopup,
      clientAddress: payload?.address || '',
      open: true,
    },
  }))
  .handleAction(openConfirmReceivedAmountPopup, (state, { payload: { open, walletId } }) => ({
    ...state,
    confirmReceivedAmount: {
      ...state.confirmReceivedAmount,
      open,
      walletId: walletId || null,
      ...(!open && { ...initialState.confirmReceivedAmount }),
    },
  }))
  .handleAction(confirmReceivedAmountRequest, (state) => ({
    ...state,
    confirmReceivedAmount: { ...state.confirmReceivedAmount, status: 'LOADING' },
  }))
  .handleAction(confirmReceivedAmountSuccess, (state) => ({
    ...state,
    confirmReceivedAmount: {
      ...initialState.confirmReceivedAmount,
      status: 'SUCCESS',
    },
  }))
  .handleAction(confirmReceivedAmountFailure, (state) => ({
    ...state,
    confirmReceivedAmount: { ...state.confirmReceivedAmount, status: 'ERROR' },
  }))
  .handleAction(openRequestSettlementPopup, (state, { payload: { open } }) => ({
    ...state,
    requestSettlement: {
      ...state.requestSettlement,
      open,
      ...(!open && { ...initialState.requestSettlement }),
    },
  }))
  .handleAction(createSettlementRequest, (state) => ({
    ...state,
    requestSettlement: {
      ...state.requestSettlement,
      status: 'LOADING',
    },
  }))
  .handleAction(createSettlementSuccess, (state, { payload }) => ({
    ...state,
    requestSettlement: {
      ...state.requestSettlement,
      status: 'SUCCESS',
      data: payload,
      open: initialState.requestSettlement.open,
    },
  }))
  .handleAction(createSettlementFailure, (state, { payload }) => ({
    ...state,
    requestSettlement: {
      ...state.requestSettlement,
      status: 'ERROR',
      error: payload,
    },
  }))
